// Session-cookie auth state (`sneethel.sid`). GET /auth/me is the source of
// truth for "am I logged in, and as whom" — this context just wraps it in a
// React-friendly shape and exposes login/logout mutations. Role gating
// itself is enforced by the API, never by hiding UI (admin/CLAUDE.md) — this
// context only decides what to *render* (a login form vs. the workspace),
// it is not a security boundary.

import { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient, { ApiError } from 'shared/api/client.js';

const AuthContext = createContext(null);

async function fetchMe() {
  try {
    const data = await apiClient.get('/auth/me');
    return data.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Returns EITHER { user } (unchanged — no 2FA, or 2FA already verified)
  // OR { requiresTwoFactor: true } for an admin with 2FA enabled — the
  // caller (pages/LoginPage.jsx) is responsible for switching to the code
  // step in the latter case. Deliberately does NOT setQueryData on a
  // requiresTwoFactor result — the session is not yet established
  // server-side either (routes/authRoutes.js only writes a PENDING marker,
  // never req.session.user, until the 2FA step succeeds).
  const loginMutation = useMutation({
    mutationFn: ({ email, password }) => apiClient.post('/auth/login', { email, password }),
    onSuccess: (data) => {
      if (data.user) {
        queryClient.setQueryData(['auth', 'me'], data.user);
      }
    },
  });

  // The second login step, only ever called after loginMutation resolves
  // with requiresTwoFactor: true. Same session-establishing shape as
  // loginMutation's own onSuccess.
  const verifyTwoFactorMutation = useMutation({
    mutationFn: ({ token, recoveryCode }) =>
      apiClient.post('/auth/login/2fa/verify', { token, recoveryCode }),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data.user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => apiClient.post('/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
    },
  });

  const value = useMemo(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isError: meQuery.isError,
      login: loginMutation.mutateAsync,
      isLoggingIn: loginMutation.isPending,
      loginError: loginMutation.error,
      verifyTwoFactor: verifyTwoFactorMutation.mutateAsync,
      isVerifyingTwoFactor: verifyTwoFactorMutation.isPending,
      verifyTwoFactorError: verifyTwoFactorMutation.error,
      logout: logoutMutation.mutateAsync,
    }),
    [
      meQuery.data,
      meQuery.isLoading,
      meQuery.isError,
      loginMutation,
      verifyTwoFactorMutation,
      logoutMutation,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>.');
  }
  return ctx;
}
